/** Typed task wrappers. Client-side zod schemas mirror the proxy's — the client
 *  trusts nothing it did not validate itself (§8). */
import { z } from 'zod';
import { callTask } from './client';
import type { InventoryItem, PriceItem, TakeoffLine } from '../core/types';
import type { AerialCapture } from '../providers/aerial';

// ---------- scope_parse (§5.5) ----------
export const ScopeLineSchema = z.object({
  item: z.string(),                      // price book slug the model matched
  qty: z.number().nonnegative(),
  label: z.string().max(120),
  confidence: z.enum(['high', 'medium', 'low']),
});
export const ScopeResultSchema = z.object({
  lines: z.array(ScopeLineSchema).max(60),
  skipped: z.array(z.object({ text: z.string().max(200), reason: z.string().max(200) })).max(60),
});
export type ScopeResult = z.infer<typeof ScopeResultSchema>;

/**
 * David writes lists — bullets, numbers, one item per line. The model reads a single
 * text field, so normalize here rather than demanding a particular format from him
 * (field feedback 2026-07-25). Done client-side so no proxy redeploy is required.
 */
export function normalizeScopeText(raw: string): string {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*•·–—]|\d+[.)])\s*/, '').trim())
    .filter(Boolean);
  return lines.length > 1 ? lines.join('; ') : (lines[0] ?? raw.trim());
}

export function scopeParse(text: string, prices: PriceItem[]) {
  const catalog = prices.filter((p) => !p.retired).map((p) => ({ id: p.itemId, name: p.name, unit: p.unit, kind: p.kind }));
  return callTask('scope_parse', { text: normalizeScopeText(text), catalog }, ScopeResultSchema);
}

// ---------- bid_map (§5.4) ----------
export const BidMapResultSchema = z.object({
  lines: z.array(z.object({
    item: z.string(),
    qty: z.number().nonnegative(),
    label: z.string().max(120),
    basis: z.string().max(240),          // which scanned feature and geometry the qty comes from
    confidence: z.enum(['high', 'medium', 'low']),
  })).max(60),
  notes: z.string().max(1000).optional(),
});
export type BidMapResult = z.infer<typeof BidMapResultSchema>;

export function bidMap(
  inventory: InventoryItem[], lotSqFt: number | undefined, prices: PriceItem[],
  candidates: Record<string, string[]>,
) {
  const inv = inventory.filter((i) => !i.deletedAt).map((i) => ({
    cat: i.cat, species: i.species, qty: i.qty, notes: i.notes, source: i.provenance.source,
  }));
  const catalog = prices.filter((p) => !p.retired).map((p) => ({ id: p.itemId, name: p.name, unit: p.unit, kind: p.kind }));
  return callTask('bid_map', { inventory: inv, lotSqFt, catalog, candidates }, BidMapResultSchema);
}

// ---------- photo_inventory (§5.3) ----------
export const PhotoInvSchema = z.object({
  items: z.array(z.object({
    cat: z.enum(['tree', 'palm', 'shrub', 'cactus', 'hedge', 'bed', 'turf', 'valve', 'drain', 'other']),
    species: z.string().max(80).optional(),
    qty: z.number().positive().max(500),
    cond: z.enum(['good', 'fair', 'poor', 'dead', 'unknown']),
    notes: z.string().max(300),
    confidence: z.enum(['high', 'medium', 'low']),
  })).max(80),
  caveats: z.string().max(500).optional(), // e.g. "back yard not visible"
});
export type PhotoInvResult = z.infer<typeof PhotoInvSchema>;

export function photoInventory(imagesB64: { mime: string; b64: string }[]) {
  return callTask('photo_inventory', { images: imagesB64 }, PhotoInvSchema);
}

// ---------- aerial_inventory (field request 2026-07-25) ----------
/** Same row shape as a photo inventory — these land in the same place and carry
 *  the same unverified status — but read from a county aerial instead of a phone
 *  photo, because OpenStreetMap has no tree data in Phoenix subdivisions. */
export const AerialInvSchema = z.object({
  items: z.array(z.object({
    cat: z.enum(['tree', 'palm', 'shrub', 'cactus', 'hedge', 'bed', 'turf', 'valve', 'drain', 'other']),
    species: z.string().max(80).optional(),
    // the proxy already normalised this; the client must accept the range the
    // proxy can emit, or a valid answer dies on the doorstep
    qty: z.number().nonnegative().max(5000),
    cond: z.enum(['good', 'fair', 'poor', 'dead', 'unknown']),
    notes: z.string().max(300),
    confidence: z.enum(['high', 'medium', 'low']),
  })).max(80),
  caveats: z.string().max(1200).optional(),
});
export type AerialInvResult = z.infer<typeof AerialInvSchema>;

/**
 * Build the request separately from sending it, so a test can put it through the
 * proxy's own input schema. Three fields here were rejected in production as
 * invalid_request because the client and the proxy disagreed about their limits:
 * a frame label over 40 characters, and two `.positive()` numbers that round to
 * zero on a small tract. Mocked end-to-end tests cannot catch that class of
 * fault — only checking the real contract can.
 */
export function aerialInventoryInput(cap: AerialCapture, place?: string) {
  const avgCmPerPx = cap.frames.reduce((s, f) => s + f.cmPerPx, 0) / cap.frames.length;
  return {
    images: cap.frames.map((f) => ({ mime: f.mime, b64: f.b64 })),
    frames: cap.frames.map((f) => f.label.slice(0, 40)),
    place: place ? place.slice(0, 160) : undefined,
    // both are declared positive on the proxy: a quarter-acre island rounds to
    // zero acres, and a tight frame rounds to zero centimetres per pixel
    acres: Math.max(0.01, Math.round(cap.acres * 100) / 100),
    cmPerPx: Math.max(1, Math.round(avgCmPerPx)),
    layout: cap.layout,
  };
}

export function aerialInventory(cap: AerialCapture, place?: string) {
  return callTask('aerial_inventory', aerialInventoryInput(cap, place), AerialInvSchema);
}

// ---------- proposal_draft (§5.6) ----------
export const ProposalDraftSchema = z.object({
  narrative: z.string().max(4000),
  exclusions: z.string().max(2000).optional(),
  assumptions: z.string().max(2000).optional(),
});

export function proposalDraft(lines: { name: string; label?: string; qty: number; unit: string }[], priorNarratives: string[], jobTitle: string, siteAddress: string) {
  return callTask('proposal_draft', { lines, priorNarratives: priorNarratives.slice(0, 3), jobTitle, siteAddress }, ProposalDraftSchema);
}

// ---------- email_draft (§5.9) ----------
export const EmailDraftSchema = z.object({ subject: z.string().max(200), body: z.string().max(4000) });

export function emailDraft(kind: 'bid_follow_up' | 'schedule_change' | 'progress_summary', context: Record<string, string>) {
  return callTask('email_draft', { kind, context }, EmailDraftSchema);
}

// ---------- sanity summary (§5.7 — rules run locally and free; AI only phrases them) ----------
export const SanitySummarySchema = z.object({ summary: z.string().max(1200) });

export function sanitySummary(flags: string[], jobTitle: string) {
  return callTask('sanity_check', { flags, jobTitle }, SanitySummarySchema);
}

// ---------- field_notes (§5.8) ----------
export const FieldNotesSchema = z.object({
  transcript: z.string().max(8000),
  inventory: PhotoInvSchema.shape.items,
  verifications: z.array(z.object({ lineHint: z.string().max(120), note: z.string().max(300) })).max(40),
});

export function fieldNotes(audio: { mime: string; b64: string }) {
  return callTask('field_notes', { audio }, FieldNotesSchema);
}

export type { TakeoffLine };
